import React, { useMemo, useState, useEffect, useImperativeHandle } from 'react'
import { HtmlContent, PopupMenuItem, makeStyles, DescriptionIcon } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import type { QuoteDocument } from 'paintscout'
import { HtmlEditor, Placeholder, PresEditorTooltip } from '../../components'
import { getPresentationTemplateContext } from '../../util'
import {
  buildPlaceholderLabelMap,
  templatePlaceholderLineHeight,
  templatePlaceholderStyles,
  templateToPlaceholderHtml,
  useClientOptions
} from '@paintscout/ui-business'
import { getTemplateTags } from '@paintscout/util/util'
import { template } from '@paintscout/util/templater'
import SyncIcon from '@material-ui/icons/Sync'
import classnames from 'classnames'
import { usePresentation, useQuote } from '../../../context'

export interface SmartContentOption {
  key: string
  label: string
  resolve: (quote: QuoteDocument) => string | null
}

interface InlineEditableHtmlProps {
  content: string
  placeholderButtonText?: string
  handleEdit: (content: string) => void
  classes?: StyleClasses<typeof useStyles>
  isEditable?: boolean
  isSettings?: boolean
  header?: boolean
  presetColors?: string[]
  /** Available "smart content" sources (e.g. "quote/job address") this field can bind to. */
  smartContentOptions?: SmartContentOption[]
  /** Currently-selected smart content key (null/undefined = manual). */
  smartContentKey?: string | null
  /** Persists the smart content selection (a sibling field, alongside the manual content). */
  onSmartContentChange?: (key: string | null) => void
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      position: 'relative',
      '& .ProseMirror p': {
        '&:after': {
          display: 'none'
        }
      },
      color: theme.palette.text.primary,
      // Chips rendered read-only (unfocused fields, while the editor is open) get the same look as
      // the ones inside the RTE — the styles live in one place so they cannot drift.
      '& p:has(.template-placeholder)': templatePlaceholderLineHeight,
      '& .template-placeholder': templatePlaceholderStyles(theme)
    },
    editorRoot: {
      cursor: 'text',
      minHeight: '0 !important',
      border: 'none !important',
      color: 'inherit',
      overflow: 'hidden'
    },
    placeholder: {},
    editor: {
      '& div': {
        padding: '0 !important',
        '& p': {
          margin: '0 !important'
        }
      }
    },
    smartContentRow: {
      backgroundColor: theme.palette.grey[100],
      borderRadius: theme.borderRadius.md,
      boxShadow: theme.boxShadow[1],
      display: 'flex',
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      right: theme.spacing(2),
      top: theme.spacing(2),
      zIndex: 3
    }
  }
})

export type InlineEditableHtmlRef = {
  getContent: () => string
}

const InlineEditableHtml = React.forwardRef(function InlineEditableHtml(
  props: InlineEditableHtmlProps,
  ref: React.Ref<InlineEditableHtmlRef>
) {
  const classes = useStyles(props)
  const {
    handleEdit,
    isSettings,
    isEditable,
    placeholderButtonText,
    header,
    presetColors,
    smartContentOptions,
    smartContentKey,
    onSmartContentChange
  } = props
  const { quote } = useQuote()
  const { options } = useClientOptions()
  // Set only by the presentation editor page (PresentationEdit) — the signal for "we are editing
  // this presentation", as opposed to `isEditable` below which means "this field has focus".
  const { isEditable: isEditingPresentation } = usePresentation()
  const [editing, setEditing] = useState(false)
  const [content, setContent] = useState(props.content)

  // reset contents to match props, needed for undo
  useEffect(() => {
    setContent(props.content)
  }, [props.content])

  // The presentation-wide set of `{{key}}` tags (client name, company name, job address, etc.)
  // that can be inserted inline into free-typed text and resolved to real values on read.
  const templateTags = useMemo(
    () => getTemplateTags({ options, invoice: quote?.is_invoice }),
    [options, quote?.is_invoice]
  )
  const templateContext = useMemo(() => getPresentationTemplateContext({ quote, options }), [quote, options])

  // A smart content source binds this field to a live, quote-derived value (e.g. the quote/job
  // address) instead of a fixed one — resolved fresh from whichever quote is in context, so the
  // same template shows the right text per quote. The manually-typed `content` is preserved
  // independently underneath so switching the source off reverts to it. While actively editing,
  // the manual content is always what's shown/edited — smart content only takes over the
  // read/display state.
  const activeSmartContent = smartContentOptions?.find((option) => option.key === smartContentKey)
  const resolvedManualContent = useMemo(
    () => (content ? template({ text: content, context: templateContext }) : content),
    [content, templateContext]
  )
  const resolvedContent = activeSmartContent
    ? activeSmartContent.resolve(quote) ?? resolvedManualContent
    : resolvedManualContent
  const isSmartActive = !!activeSmartContent && !editing

  // While the presentation editor is open, an unfocused field shows its `{{key}}` tags as chips
  // rather than resolving them. Two reasons: tags stay visible across the whole page instead of
  // only in whichever field has focus, and there is no real quote behind the editor — the context
  // resolves to empty strings, so resolving here made tags vanish the moment a field lost focus.
  // This is a plain string → HTML transform rendered by HtmlContent; no editor is mounted for it.
  const showTagsUnresolved = isEditingPresentation && !activeSmartContent
  const placeholderContent = useMemo(
    () => (content ? templateToPlaceholderHtml(content, buildPlaceholderLabelMap(templateTags)) : content),
    [content, templateTags]
  )
  const displayContent = showTagsUnresolved ? placeholderContent : resolvedContent

  const nullishContent = !content || content === '<p></p>'
  // Emptiness has to be judged on what we actually render: a field holding only `{{clientName}}`
  // resolves to '' but is not empty when shown as a chip.
  const nullishDisplayContent = !displayContent || displayContent === '<p></p>'

  useImperativeHandle(ref, () => ({
    getContent: () => {
      return content || props.content
    }
  }))

  // instead of listening for a blur on the editor, we listen for a click outside of the block
  useEffect(() => {
    if (editing) {
      handleConfirm()
    }
  }, [isEditable])

  const handleConfirm = (ev?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (ev) ev.stopPropagation()
    handleEdit(content)
    setEditing(false)
  }

  const handleChange = (content: string) => {
    setContent(content)
  }

  // Template tags are inserted from the RTE's own bubble/floating menus (see EditorTemplateMenu),
  // so they live next to the caret rather than in this corner row.
  const editorToolsRow = smartContentOptions?.length > 0 && !isSmartActive && (
    <div className={classnames(classes.smartContentRow)} onClick={(ev) => ev.stopPropagation()}>
      {smartContentOptions.map((option) => (
        <PresEditorTooltip key={option.key} title={option.label}>
          <PopupMenuItem
            icon={SyncIcon}
            iconOnly
            selected={smartContentKey === option.key}
            onClick={() => onSmartContentChange?.(smartContentKey === option.key ? null : option.key)}
          />
        </PresEditorTooltip>
      ))}
    </div>
  )

  return (
    <>
      {isEditable && (
        <div
          onClick={() => {
            if (!editing) setEditing(true)
          }}
          className={classes.root}
        >
          {editorToolsRow}
          {isSmartActive && <HtmlContent content={resolvedContent ?? ''} />}
          {!isSmartActive && (!nullishContent || editing) && (
            // wrapping the RTE in a div (somehow) prevents errors from improper FloatingMenu cleanup on RTE unmount
            <div>
              <HtmlEditor
                tippyOptions={{ appendTo: document.querySelector('#BasePage-root'), zIndex: 4 }}
                header={header}
                value={content}
                classes={{ root: classes.editorRoot, editor: classes.editor }}
                onChange={handleChange}
                autofocus={nullishContent ? 'start' : null}
                hasColor={true}
                presetColors={presetColors}
                templateTags={templateTags}
                disableToolbar
              />
            </div>
          )}
          {!isSmartActive && nullishContent && !editing && (
            <Placeholder
              classes={{ placeholder: classes.placeholder }}
              Icon={DescriptionIcon}
              buttonText={placeholderButtonText}
            />
          )}
        </div>
      )}
      {!isEditable && (
        <>
          {!nullishDisplayContent && <HtmlContent className={classes.root} content={displayContent} />}
          {nullishDisplayContent && isSettings && (
            <Placeholder
              classes={{ placeholder: classes.placeholder }}
              Icon={DescriptionIcon}
              buttonText={placeholderButtonText}
            />
          )}
        </>
      )}
    </>
  )
})

export default InlineEditableHtml
